import { stringify } from 'qs';
import { groupBy } from 'remeda';

import { NudgeSchemaTrigger } from '@grow-therapy-team/seedling-components';

import { getAppConfig } from '../../config';
import { EventTypeEnum } from '../../segment/types';
import { AppointmentType } from '../schedule-preview/types';
import { getAppointmentTypeText } from '../schedule-preview/utils';
import { ProviderDrawerState } from '../state';
import { GetCurrentNoteSchemaResponse } from '../useGetCurrentNoteSchema';
import { TimelineAppointment, TimelineAppointmentEdge } from '../useGetUpcomingAppointments';
import { CLIENT_INFO_HEADER_TITLE, CLIENT_INFO_HEADER_TITLE_FALLBACK } from './lang';
import { FieldTypeEnum, NoteResponseType, NoteType } from './types';

export function formatNotes(
  notes: NoteResponseType,
  currentNoteSchema?: GetCurrentNoteSchemaResponse,
): Record<string, NoteType[]> {
  if (!currentNoteSchema || !notes) return {};
  const formattedNotes: Record<string, NoteType[]> = {};

  currentNoteSchema.currentNoteSchema.sections.forEach((section) => {
    formattedNotes[section.label] = [];

    section.fields.forEach((field) => {
      let answer: string | string[] | boolean = '';
      if (field.fieldType === FieldTypeEnum.CHECKBOX) {
        answer =
          field.name === 'risk_issues' && notes[field.name] === false
            ? 'No suicidal or homicidal ideation suspected or disclosed'
            : notes[field.name];
      } else if (field.fieldType === FieldTypeEnum.MULTISELECT) {
        answer = Array.isArray(notes[field.name]) ? notes[field.name] : [];
      } else {
        answer = notes[field.name] || answer;
      }

      let details: string[] = [];

      // Many questions have supplemental questions that are appended by _details.
      // It's easier to keep track of by storing these details in the same section object rather than treating it like a separate question
      const baseFieldName = field.name.replace(/_details$/, '');
      const isDetailsField = baseFieldName !== field.name;

      if (isDetailsField) {
        const fieldValue = notes[field.name];
        details = Array.isArray(fieldValue) ? fieldValue : ([fieldValue] as string[]).filter(Boolean);

        const baseFieldFormattedSection = formattedNotes[section.label].find(
          (sectionItem) => sectionItem.name === baseFieldName,
        );

        if (baseFieldFormattedSection) {
          baseFieldFormattedSection.details = details;
        }
      } else {
        const formattedSection: NoteType = {
          question: field.label,
          name: field.name,
          answer: answer,
          margin: field.margin,
          textVariant: field.textVariant,
          fieldType: field.fieldType as FieldTypeEnum,
          details: details,
        };
        formattedNotes[section.label].push(formattedSection);
      }
    });
  });

  return formattedNotes;
}

/** Groups upcoming appointments so that recurring appointments show only the next upcoming entry and sorts them in date order */
export function formatUpcomingAppointments(appointments: TimelineAppointmentEdge[] | undefined): TimelineAppointment[] {
  const groupedAppointments = appointments
    ? groupBy(appointments, (appointment) => appointment?.node?.recurringAppointmentGroupingId ?? appointment?.node?.id)
    : {};
  const upcomingAppointments = Object.values(groupedAppointments).map((appointmentGroup) => appointmentGroup[0].node);
  return upcomingAppointments.sort((a, b) => {
    return new Date(a.timeStart).getTime() - new Date(b.timeStart).getTime();
  });
}

/**
 * Creates click handler functions for different nudge triggers.
 *
 * @param nudges - Array of nudge objects
 * @param setDrawerState - Function to update drawer state
 * @param providerDashboardReferralUri - URI for provider dashboard referral
 * @param userClicked - Event logging function
 * @param isInSessionWithSelectedClient - Flag indicating if in session with selected client
 * @param appointmentShortId - Appointment short ID for tracking
 * @param source - Where the nudge was shown
 * @returns Mapping of nudge triggers to their click handler functions
 */
export function createNudgeOnClickHandlers(
  nudges: Array<{
    id: number;
    nudgeSchema: { trigger: NudgeSchemaTrigger };
  }>,
  setDrawerState: (state: ProviderDrawerState) => void,
  providerDashboardReferralUri: string,
  userClicked: (
    eventType: EventTypeEnum,
    payload: {
      nudgeId: number;
      appointmentShortId?: string;
      source?: 'telehealth-session' | 'telehealth-waiting-room';
    },
  ) => void,
  isInSessionWithSelectedClient: boolean,
  appointmentShortId?: string,
  source?: 'telehealth-session' | 'telehealth-waiting-room',
): Partial<Record<NudgeSchemaTrigger, () => void>> | undefined {
  if (!nudges.length) return;

  const triggerHandlers: Partial<Record<NudgeSchemaTrigger, (nudgeId: number) => void>> = {
    // NOTE: This is a conditional handler that only gets defined if the provider is in session with the selected client
    ...(isInSessionWithSelectedClient
      ? {
          [NudgeSchemaTrigger.CLINICAL_RISK_REVIEW]: (nudgeId) => {
            setDrawerState(ProviderDrawerState.CSSRS_FORM);
            userClicked(EventTypeEnum.PROVIDER_NUDGE_CLICKED, {
              nudgeId,
              appointmentShortId,
              source: 'telehealth-session',
            });
          },
        }
      : {}),
    ...(providerDashboardReferralUri
      ? {
          [NudgeSchemaTrigger.PSYCHIATRIC_MEDICATION_EVAL_WITHOUT_SUICIDALITY]: (nudgeId) => {
            window.open(providerDashboardReferralUri, '_blank', 'noopener,noreferrer');
            userClicked(EventTypeEnum.PROVIDER_NUDGE_CLICKED, {
              nudgeId,
              appointmentShortId,
              source,
            });
          },
        }
      : {}),
  };

  const nudgeHandlers = nudges
    .filter((nudge) => triggerHandlers[nudge.nudgeSchema.trigger] !== undefined)
    .map((nudge) => [nudge.nudgeSchema.trigger, () => triggerHandlers[nudge.nudgeSchema.trigger]!(nudge.id)]);

  return nudgeHandlers.length === 0 ? undefined : Object.fromEntries(nudgeHandlers);
}

export function getProviderDashboardReferralUri(patientId: string): string {
  return `${getAppConfig().providerOrigin}/refer-client/filters/${patientId}`;
}

const KNOWN_APPOINTMENT_TYPES = new Set<string>(Object.values(AppointmentType));

export type AppointmentTypeCandidate = {
  patientShortId?: string;
  appointmentType?: string | null;
};

export type LastAppointmentForTypeResolution = {
  id?: string;
  appointmentType?: string | null;
  timeStart?: unknown;
  timeEnd?: unknown;
};

// Chart lastAppointment can describe either the previous, already-completed visit or
// the current visit once it has started -- there's no way to tell which just from its
// shape. Its own appointmentType is only meaningful in the latter case, so it's only
// trusted once its time window confirms it's the visit happening right now.
function isAppointmentUnderway(
  appointment?: Pick<LastAppointmentForTypeResolution, 'timeStart' | 'timeEnd'> | null,
  now: Date = new Date(),
): boolean {
  if (!appointment?.timeStart || !appointment?.timeEnd) return false;

  return new Date(appointment.timeStart as string) <= now && now <= new Date(appointment.timeEnd as string);
}

/**
 * Single source of truth for resolving an appointment's type. `candidates` are tried in
 * priority order and only used when they name the patient we're resolving for. When none
 * apply, falls back to the patient chart's lastAppointment -- but its own appointmentType
 * is only trusted once its time window confirms it IS the visit being resolved (not an
 * earlier, unrelated one); otherwise only its existence is used, to infer "this patient
 * has been seen before" -> follow-up.
 *
 * A missing lastAppointment is ambiguous: it either means the chart query hasn't
 * returned yet, or it means the patient genuinely has no prior visit. Those two cases
 * must not both fall back to guessing Intake, or callers render an Intake-shaped UI
 * (e.g. the intake summary card) that then flips to Follow-up/Consultation once the
 * chart loads. Pass `isLastAppointmentLoading` so the function can return `undefined`
 * -- "not yet resolvable" -- instead of guessing while that query is in flight.
 */
export function resolveAppointmentType({
  patientShortId,
  candidates,
  lastAppointment,
  isLastAppointmentLoading = false,
}: {
  patientShortId?: string;
  candidates: AppointmentTypeCandidate[];
  lastAppointment?: LastAppointmentForTypeResolution | null;
  isLastAppointmentLoading?: boolean;
}): AppointmentType | undefined {
  const explicitType = candidates.find(
    (candidate) => !!patientShortId && candidate.patientShortId === patientShortId && candidate.appointmentType,
  )?.appointmentType;

  if (explicitType && KNOWN_APPOINTMENT_TYPES.has(explicitType)) {
    return explicitType as AppointmentType;
  }

  const chartAppointmentType = lastAppointment?.appointmentType;
  if (
    isAppointmentUnderway(lastAppointment) &&
    chartAppointmentType &&
    KNOWN_APPOINTMENT_TYPES.has(chartAppointmentType)
  ) {
    return chartAppointmentType as AppointmentType;
  }

  if (!lastAppointment?.id && isLastAppointmentLoading) {
    return undefined;
  }

  return lastAppointment?.id ? AppointmentType.FollowUp : AppointmentType.Intake;
}

export function getAppointmentClientInfoHeader(appointmentType: AppointmentType | undefined, visitorName?: string) {
  return visitorName && appointmentType
    ? CLIENT_INFO_HEADER_TITLE.replace('%appointmentType', getAppointmentTypeText(appointmentType)).replace(
        '%visitorName',
        visitorName,
      )
    : CLIENT_INFO_HEADER_TITLE_FALLBACK;
}

export function getReferClientLink(patientShortId: string): string {
  return `${getAppConfig().providerOrigin}/refer-client/filters/${patientShortId}`;
}

/**
 * Deep link to the medications tab of a client's chart on the provider dashboard. The chart
 * lives on a different origin than telehealth, so this can only be opened in a new tab.
 */
export function getClientChartMedicationsLink(patientId: string): string {
  return `${getAppConfig().providerOrigin}/patient?${stringify({ id: patientId, tab: 'medications' })}`;
}
